import React, { useState, useRef, useEffect, useCallback } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, TextField, Button, Grid, MenuItem, Rating, Chip } from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SendIcon from '@mui/icons-material/Send';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RateReviewIcon from '@mui/icons-material/RateReview';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const GOLD = '#d4af37';

const categories = [
  'Temple Services',
  'Pooja Experience',
  'Staff Behavior',
  'Cleanliness',
  'Facilities',
  'Website',
  'General Feedback',
  'Complaint',
  'Suggestion',
];

const whatNext = [
  { text: 'Your feedback is reviewed by our management team', Icon: VerifiedIcon },
  { text: 'Valid complaints are addressed within 48 hours', Icon: ScheduleIcon },
  { text: 'You will receive a response via email', Icon: MarkEmailReadIcon },
  { text: 'Suggestions are considered for temple improvements', Icon: BuildCircleIcon },
];

export default function Feedback() {
  const { user } = useAuth();
  const [rating, setRating] = useState<number | null>(4);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    subject: '',
    message: '',
  });
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const TURNSTILE_SITE_KEY = '0x4AAAAAACpmoaG-ENnK6t7-';

  const renderTurnstile = useCallback(() => {
    if (!turnstileRef.current || !window.turnstile) return;
    if (widgetIdRef.current !== null) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }
    widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => setTurnstileToken(token),
      'expired-callback': () => setTurnstileToken(null),
      'error-callback': () => setTurnstileToken(null),
      theme: 'light',
    });
  }, [TURNSTILE_SITE_KEY]);

  useEffect(() => {
    if (window.turnstile) {
      renderTurnstile();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          renderTurnstile();
        }
      }, 300);
      return () => clearInterval(interval);
    }
    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [renderTurnstile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) {
      alert('Please complete the verification before submitting.');
      return;
    }
    setSending(true);
    try {
      await addDoc(collection(db, 'contact_messages'), {
        ...formData,
        rating,
        formType: 'feedback',
        read: false,
        createdAt: serverTimestamp(),
        ...(user ? { userId: user.uid, userEmail: user.email } : {}),
      });
      alert('Thank you for your feedback! We will review it carefully.');
      setFormData({ name: '', email: '', phone: '', category: '', subject: '', message: '' });
      setRating(4);
    } catch (err: any) {
      alert('Error submitting feedback: ' + (err?.message || 'Please try again later.'));
    } finally {
      setSending(false);
      setTurnstileToken(null);
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      fontSize: '0.85rem',
      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
      '&:hover fieldset': { borderColor: '#ec4899' },
      '&.Mui-focused fieldset': { borderColor: '#ec4899', borderWidth: 2 },
    },
    '& .MuiInputLabel-root': { fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#ec4899' },
  };

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Feedback — Kottayil Temple" description="Share your feedback and suggestions for Kottayil Temple (Kotta-Kovilakam Kshethram). We value devotee experience and input." path="/contact/feedback" keywords="Kottayil temple feedback, suggestions, devotee feedback, കൊട്ടയിൽ ക്ഷേത്രം ഫീഡ്ബാക്ക്" />
      <PageBanner title="Feedback Form" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #831843 0%, #be185d 40%, #ec4899 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <FeedbackIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Feedback Form
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      Your feedback helps us improve our services
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  We value every devotee's opinion. Share your experience, suggestions, or concerns so we can serve you better.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<RateReviewIcon sx={{ fontSize: '16px !important', color: '#ec4899 !important' }} />} label="Rate & Review" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#831843', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<ThumbUpAltIcon sx={{ fontSize: '16px !important', color: '#f9a8d4 !important' }} />} label="Anonymous Option" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {/* FORM CARD */}
            <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', mb: 4 }}>
              <Box sx={{ background: 'linear-gradient(135deg, #ec4899, #db2777)', px: 3, py: 2, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: '12px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                    <SendIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>Share Your Feedback</Typography>
                </Box>
              </Box>
              <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Box component="form" onSubmit={handleSubmit}>
                  {/* RATING */}
                  <Box sx={{ textAlign: 'center', mb: 3, py: 2.5, px: 2, bgcolor: 'rgba(236,72,153,0.04)', borderRadius: '18px', border: '1px solid rgba(236,72,153,0.1)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8, mb: 1 }}>
                      <StarIcon sx={{ color: '#ec4899', fontSize: 18 }} />
                      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#1e293b' }}>Rate Your Experience</Typography>
                    </Box>
                    <Rating
                      value={rating}
                      onChange={(_, newValue) => setRating(newValue)}
                      size="large"
                      sx={{
                        fontSize: { xs: '1.8rem', sm: '2.5rem' },
                        '& .MuiRating-iconFilled': { color: '#ec4899' },
                        '& .MuiRating-iconHover': { color: '#f472b6' },
                      }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Full Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required size="small" sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required size="small" sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Phone Number" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} size="small" sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth select label="Feedback Category" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} required size="small" sx={inputSx}>
                        {categories.map((cat) => (
                          <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required size="small" sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Your Feedback / Suggestions" multiline rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required placeholder="Please share your thoughts, experiences, or suggestions..." size="small" sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <Box ref={turnstileRef} sx={{ display: 'flex', justifyContent: 'center', my: 1 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" variant="contained" fullWidth disabled={!turnstileToken || sending} startIcon={<SendIcon sx={{ fontSize: 16 }} />} sx={{ background: 'linear-gradient(135deg, #ec4899, #db2777)', color: '#fff', fontWeight: 700, borderRadius: '14px', py: 1.2, fontSize: '0.85rem', textTransform: 'none', boxShadow: '0 6px 20px rgba(236,72,153,0.3)', '&:hover': { background: 'linear-gradient(135deg, #db2777, #be185d)', transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(236,72,153,0.4)' }, '&.Mui-disabled': { background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)', boxShadow: 'none' } }}>
                        {sending ? 'Submitting...' : 'Submit Feedback'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>

            {/* WHAT HAPPENS NEXT FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <CheckCircleIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>What Happens Next?</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Our feedback process explained</Typography>
                  </Box>
                </Box>
                {whatNext.map((item, i) => {
                  const WIcon = item.Icon;
                  return (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8, px: 1.5, borderRadius: '12px', transition: 'all 250ms', '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: `${GOLD}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <WIcon sx={{ fontSize: 14, color: GOLD }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', fontWeight: 500, lineHeight: 1.5 }}>{item.text}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="contact" activePath="/contact/feedback" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
